import { useState, useEffect, useRef } from 'react';
import { useCounter } from './animations';
import { supabase } from './supabaseClient';
import { useProfile } from './ProfileContext';
import './Card.css';
import './Dashboard.css';
import AppNav from './AppNav';
import OccupationSelector from './OccupationSelector';
import SecondaryOccupation from './SecondaryOccupation';
import CivilianTranslation from './CivilianTranslation';
import { saveResumeVersion } from './resumeUpload';
import { normalizeBranch } from './branch';

// ── Civilian-first identity rendering ──────────────────────────
// Composes a polished display label from the user's chosen display_identity
// category code plus their branch. Stable storage codes; rendering changes
// without database migrations.
function renderIdentity(identity, branch) {
  const b = normalizeBranch(branch);
  switch (identity) {
    case 'retired_veteran':
      return b ? `Retired ${b} Veteran` : 'Retired Veteran';
    case 'veteran':
      return b ? `${b} Veteran` : 'Veteran';
    case 'transitioning_service_member':
      return b ? `Transitioning ${b} Service Member` : 'Transitioning Service Member';
    case 'active_duty':
      return b ? `Active Duty ${b}` : 'Active Duty';
    case 'guard_reserve':
      return b ? `${b} Guard/Reserve` : 'Guard/Reserve';
    case 'military_spouse':
      return 'Military Spouse';
    case 'family_member':
      return 'Military Family Member';
    case 'supporter':
      return 'VetQualified Supporter';
    default:
      return 'VetQualified Member';
  }
}

const RANKS_BY_BRANCH = {
  'U.S. Army': [
    'E-1 · Private','E-2 · Private Second Class','E-3 · Private First Class',
    'E-4 · Specialist / Corporal','E-5 · Sergeant','E-6 · Staff Sergeant',
    'E-7 · Sergeant First Class','E-8 · Master Sergeant / First Sergeant',
    'E-9 · Sergeant Major / Command Sergeant Major / Sergeant Major of the Army',
    'W-1 · Warrant Officer 1','W-2 · Chief Warrant Officer 2','W-3 · Chief Warrant Officer 3',
    'W-4 · Chief Warrant Officer 4','W-5 · Chief Warrant Officer 5',
    'O-1 · Second Lieutenant','O-2 · First Lieutenant','O-3 · Captain',
    'O-4 · Major','O-5 · Lieutenant Colonel','O-6 · Colonel',
    'O-7 · Brigadier General','O-8 · Major General',
    'O-9 · Lieutenant General','O-10 · General',
  ],
  'U.S. Marine Corps': [
    'E-1 · Private','E-2 · Private First Class','E-3 · Lance Corporal',
    'E-4 · Corporal','E-5 · Sergeant','E-6 · Staff Sergeant',
    'E-7 · Gunnery Sergeant','E-8 · Master Sergeant / First Sergeant',
    'E-9 · Master Gunnery Sergeant / Sergeant Major',
    'W-1 · Warrant Officer 1','W-2 · Chief Warrant Officer 2','W-3 · Chief Warrant Officer 3',
    'W-4 · Chief Warrant Officer 4','W-5 · Chief Warrant Officer 5',
    'O-1 · Second Lieutenant','O-2 · First Lieutenant','O-3 · Captain',
    'O-4 · Major','O-5 · Lieutenant Colonel','O-6 · Colonel',
    'O-7 · Brigadier General','O-8 · Major General',
    'O-9 · Lieutenant General','O-10 · General',
  ],
  'U.S. Navy': [
    'E-1 · Seaman Recruit','E-2 · Seaman Apprentice','E-3 · Seaman',
    'E-4 · Petty Officer Third Class','E-5 · Petty Officer Second Class',
    'E-6 · Petty Officer First Class','E-7 · Chief Petty Officer',
    'E-8 · Senior Chief Petty Officer','E-9 · Master Chief Petty Officer',
    'W-1 · Warrant Officer 1','W-2 · Chief Warrant Officer 2','W-3 · Chief Warrant Officer 3',
    'W-4 · Chief Warrant Officer 4','W-5 · Chief Warrant Officer 5',
    'O-1 · Ensign','O-2 · Lieutenant Junior Grade','O-3 · Lieutenant',
    'O-4 · Lieutenant Commander','O-5 · Commander','O-6 · Captain',
    'O-7 · Rear Admiral Lower Half','O-8 · Rear Admiral Upper Half',
    'O-9 · Vice Admiral','O-10 · Admiral',
  ],
  'U.S. Air Force': [
    'E-1 · Airman Basic','E-2 · Airman','E-3 · Airman First Class',
    'E-4 · Senior Airman','E-5 · Staff Sergeant','E-6 · Technical Sergeant',
    'E-7 · Master Sergeant','E-8 · Senior Master Sergeant',
    'E-9 · Chief Master Sergeant',
    'O-1 · Second Lieutenant','O-2 · First Lieutenant','O-3 · Captain',
    'O-4 · Major','O-5 · Lieutenant Colonel','O-6 · Colonel',
    'O-7 · Brigadier General','O-8 · Major General',
    'O-9 · Lieutenant General','O-10 · General',
  ],
  'U.S. Space Force': [
    'E-1 · Specialist 1','E-2 · Specialist 2','E-3 · Specialist 3',
    'E-4 · Specialist 4','E-5 · Sergeant','E-6 · Technical Sergeant',
    'E-7 · Master Sergeant','E-8 · Senior Master Sergeant',
    'E-9 · Chief Master Sergeant',
    'O-1 · Second Lieutenant','O-2 · First Lieutenant','O-3 · Captain',
    'O-4 · Major','O-5 · Lieutenant Colonel','O-6 · Colonel',
    'O-7 · Brigadier General','O-8 · Major General',
    'O-9 · Lieutenant General','O-10 · General',
  ],
  'U.S. Coast Guard': [
    'E-1 · Seaman Recruit','E-2 · Seaman Apprentice','E-3 · Seaman',
    'E-4 · Petty Officer Third Class','E-5 · Petty Officer Second Class',
    'E-6 · Petty Officer First Class','E-7 · Chief Petty Officer',
    'E-8 · Senior Chief Petty Officer','E-9 · Master Chief Petty Officer',
    'W-2 · Chief Warrant Officer 2','W-3 · Chief Warrant Officer 3',
    'W-4 · Chief Warrant Officer 4',
    'O-1 · Ensign','O-2 · Lieutenant Junior Grade','O-3 · Lieutenant',
    'O-4 · Lieutenant Commander','O-5 · Commander','O-6 · Captain',
    'O-7 · Rear Admiral Lower Half','O-8 · Rear Admiral Upper Half',
    'O-9 · Vice Admiral','O-10 · Admiral',
  ],
};

const CLEARANCE_OPTIONS = [
  'None / Expired','Public Trust','Secret',
  'Top Secret','Top Secret / SCI',
  'TS/SCI w/ CI Poly','TS/SCI w/ Full Scope Poly',
];

const BRANCH_OPTIONS = Object.keys(RANKS_BY_BRANCH);

const WORK_TYPE_OPTIONS = [
  { value: 'Remote',             label: 'Remote',             desc: 'Work from anywhere' },
  { value: 'Hybrid',             label: 'Hybrid',             desc: 'Mix of remote and on-site' },
  { value: 'On-site',            label: 'On-site',            desc: 'In-office or facility' },
  { value: 'Federal Contractor', label: 'Federal Contractor', desc: 'Government contract work' },
  { value: 'Private Sector',     label: 'Private Sector',     desc: 'Commercial companies' },
  { value: 'Open to All',        label: 'Open to All',        desc: 'No preference' },
];

const CAREER_GOAL_OPTIONS = [
  'Cybersecurity Analyst','SOC Analyst','Penetration Tester',
  'Network Security Engineer','Cloud Security Engineer',
  'Intelligence Analyst','Threat Intelligence Analyst',
  'IT Project Manager','Systems Administrator','Network Engineer',
  'Program Manager','Operations Manager','Logistics Manager',
  'Human Resources Manager','Financial Analyst','Law Enforcement',
  'Emergency Management','Aviation / UAS','Healthcare / Medical',
  'Other (specify below)',
];

function formatDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T00:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]}-${String(d.getDate()).padStart(2,'0')}-${d.getFullYear()}`;
}

const DEFAULT_VISIBILITY = {
  military: 'private', clearance: 'private',
  certifications: 'private', skills: 'private',
  contact: 'private', resume: 'private',
};

const DEFAULT_PROFILE = {
  firstName: '', lastName: '', avatarUrl: '',
  veteranVerified: false, branch: '',
  rank: '', yearsOfService: '',
  separationDate: '', clearance: '',
  careerGoal: '', careerGoalCustom: '',
  workType: '', location: '',
  bio: '',
  displayIdentity: 'veteran',
  visibility: DEFAULT_VISIBILITY,
};

// ── Time-based reveal — works reliably on iPhone Safari ──────────
function DBReveal({ children, delay = 0 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300 + delay * 400);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      style={{
        opacity:    visible ? 1 : 0,
        transform:  visible ? 'none' : 'translateY(24px)',
        transition: 'opacity 0.65s cubic-bezier(0.4,0,0.2,1), transform 0.65s cubic-bezier(0.4,0,0.2,1)',
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  );
}

// ── Animated stats component ──────────────────────────────────────
function AnimatedStats({ stats }) {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), 600);
    return () => clearTimeout(timer);
  }, []);

  const matches  = useCounter(stats.jobMatches,     2000, started);
  const views    = useCounter(stats.profileViews,   2000, started);
  const recruits = useCounter(stats.recruiterViews, 2000, started);
  return (
    <div className="db-quick-stats">
      <div className="db-stat-cell">
        <div className="db-stat-num">{started ? matches  : 0}</div>
        <div className="db-stat-tag">Matches</div>
      </div>
      <div className="db-stat-cell">
        <div className="db-stat-num">{started ? views    : 0}</div>
        <div className="db-stat-tag">Views</div>
      </div>
      <div className="db-stat-cell">
        <div className="db-stat-num">{started ? recruits : 0}</div>
        <div className="db-stat-tag">Recruiter Views</div>
      </div>
    </div>
  );
}

// Toggle component defined at module scope so its identity is stable across Dashboard re-renders.
function Toggle({ checked, onChange }) {
  return (
    <label className="db-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="db-toggle-track"></span>
    </label>
  );
}

export default function Dashboard() {
  // eslint-disable-next-line
  const [user,          setUser]         = useState(null);
  const [profile,       setProfile]      = useState({ ...DEFAULT_PROFILE });
  const [occupation,    setOccupation]   = useState(null);
  const [careerHistory, setCareerHistory] = useState([]);
  const [stats,         setStats]        = useState({ jobMatches: 0, profileViews: 0, recruiterViews: 0 });
  const [locationSuggestions, setLocationSuggestions] = useState([]);

  // ── Load Google Maps script once ─────────────────────────────────
useEffect(() => {
  if (window.google) return;
  if (document.querySelector('script[data-gmaps]')) return;
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_KEY}&libraries=places`;
  script.async = true;
  script.setAttribute('data-gmaps', 'true');
  document.head.appendChild(script);
}, []);

  // ── Close location suggestions on outside click ───────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.closest && e.target.closest('.db-toggle')) return;
      setLocationSuggestions([]);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select(
            'first_name, last_name, branch, rank, years_of_service, separation_date, ' +
            'clearance, career_goal, career_goal_custom, work_type, location, bio, ' +
            'avatar_url, veteran_verified, occupation_branch, occupation_code, occupation_title, ' +
            'career_history, job_matches, profile_views, recruiter_views, ' +
            'certifications, tech_skills, lead_skills, mentor_focus, ' +
            'open_to_opportunities, available_to_mentor, seeking_mentor, ' +
            'resume_url, resume_name, display_identity, visibility'
          )
          .eq('id', currentUser.id)
          .single();
        if (!error && profileData) {
          setProfile(prev => ({
            ...prev,
            firstName:        profileData.first_name        || prev.firstName,
            displayIdentity:  profileData.display_identity || prev.displayIdentity,
            lastName:         profileData.last_name         || prev.lastName,
            branch:           profileData.branch            || prev.branch,
            rank:             profileData.rank              || prev.rank,
            yearsOfService:   profileData.years_of_service  || prev.yearsOfService,
            separationDate:   profileData.separation_date   || prev.separationDate,
            clearance:        profileData.clearance         || prev.clearance,
            careerGoal:       profileData.career_goal       || prev.careerGoal,
            careerGoalCustom: profileData.career_goal_custom || prev.careerGoalCustom,
            workType:         profileData.work_type         || prev.workType,
            location:         profileData.location          || prev.location,
            bio:              profileData.bio               || prev.bio,
            avatarUrl:        profileData.avatar_url        || '',
            veteranVerified:  profileData.veteran_verified  || false,
            visibility:       profileData.visibility        || DEFAULT_VISIBILITY,
          }));
          if (profileData.occupation_code) {
            setOccupation({
              branch: profileData.occupation_branch,
              code:   profileData.occupation_code,
              title:  profileData.occupation_title,
            });
          }
          if (profileData.career_history && profileData.career_history.length > 0) {
            setCareerHistory(profileData.career_history);
          }
          if (profileData.certifications)  setCerts(profileData.certifications);
          if (profileData.tech_skills)     setTechSkills(profileData.tech_skills);
          if (profileData.lead_skills)     setLeadSkills(profileData.lead_skills);
          if (profileData.mentor_focus)    setMentorFocus(profileData.mentor_focus);
          if (profileData.open_to_opportunities !== null && profileData.open_to_opportunities !== undefined) setOppOpen(profileData.open_to_opportunities);
          if (profileData.available_to_mentor   !== null && profileData.available_to_mentor   !== undefined) setMentorOpen(profileData.available_to_mentor);
          if (profileData.seeking_mentor        !== null && profileData.seeking_mentor        !== undefined) setSeekOpen(profileData.seeking_mentor);
          if (profileData.resume_name)     setResumeName(profileData.resume_name);
          if (profileData.resume_url)      setResumePath(profileData.resume_url);
          setStats({
            jobMatches:     profileData.job_matches     || 0,
            profileViews:   profileData.profile_views   || 0,
            recruiterViews: profileData.recruiter_views || 0,
          });
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [modalOpen,   setModalOpen]   = useState(false);
  const [oppOpen,     setOppOpen]     = useState(true);
  const [mentorOpen,  setMentorOpen]  = useState(false);
  const [seekOpen,    setSeekOpen]    = useState(true);
  const [displayIdentitySaveStatus, setDisplayIdentitySaveStatus] = useState('idle');
  const [visibilitySaveStatus, setVisibilitySaveStatus] = useState('idle');
  const [resumeName,  setResumeName]  = useState('');
  const [resumePath,  setResumePath]  = useState('');
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeUploadError, setResumeUploadError] = useState('');
  const resumeUploadPending = useRef(false);
  const fileInputRef   = useRef(null);
  const avatarInputRef = useRef(null);

  const {
    certs,       setCerts,
    techSkills,  setTechSkills,
    leadSkills,  setLeadSkills,
    mentorFocus, setMentorFocus,
    refreshProfile,
  } = useProfile();

  const [editModal, setEditModal] = useState(null);
  const [draft,     setDraft]     = useState({});
  const [newCert,   setNewCert]   = useState('');
  const [newTech,   setNewTech]   = useState('');
  const [newLead,   setNewLead]   = useState('');

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { setModalOpen(false); setEditModal(null); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = (modalOpen || editModal) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen, editModal]);

  const handleAvatarClick = () => avatarInputRef.current.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    if (!file.type.startsWith('image/')) {
      console.error('Avatar upload rejected: file is not an image.');
      e.target.value = '';
      return;
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const filePath = `${session.user.id}/avatar-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: false });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      e.target.value = '';
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    const { error: profileError } = await supabase
      .from('profiles')
      .update({ avatar_url: publicUrl })
      .eq('id', session.user.id);

    if (profileError) {
      console.error('Profile avatar update error:', profileError);
      e.target.value = '';
      return;
    }

    setProfile(prev => ({ ...prev, avatarUrl: publicUrl }));
    refreshProfile();
    e.target.value = '';
  };

  const handleResumeChange = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file || resumeUploadPending.current) return;
    resumeUploadPending.current = true;
    setResumeUploading(true);
    setResumeUploadError('');
    try {
      const { path, name } = await saveResumeVersion(supabase, file);
      setResumeName(name);
      setResumePath(path);
      refreshProfile();
    } catch (error) {
      setResumeUploadError(error.message || 'Your resume could not be saved. Please try again.');
    } finally {
      input.value = '';
      resumeUploadPending.current = false;
      setResumeUploading(false);
    }
  };

  const handleResumeView = async () => {
    if (resumeUploadPending.current) return;
    if (!resumePath) { fileInputRef.current?.click(); return; }
    if (resumePath.startsWith('http')) {
      console.warn('Stale public URL detected in resume_url. Please re-upload your resume.');
      alert('Your resume was uploaded under an older format. Please re-upload it to view it.');
      fileInputRef.current?.click();
      return;
    }
    const { data, error } = await supabase.storage
      .from('resumes')
      .createSignedUrl(resumePath, 60);
    if (error) { console.error('Signed URL error:', error); return; }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  function handleAddSecondary(occ) {
    setCareerHistory(prev => [...prev, { ...occ, isLastHeld: false }]);
  }
  function handleRemoveSecondary(index) {
    setCareerHistory(prev => prev.filter((_, i) => i !== index));
  }

  const openCareer = () => { setModalOpen(false); setDraft({ ...profile, occupation }); setEditModal('career'); };
  const openBio    = () => { setModalOpen(false); setDraft({ bio: profile.bio }); setEditModal('bio'); };
  const openSkills = () => {
    setModalOpen(false);
    setDraft({ certs: [...certs], techSkills: [...techSkills], leadSkills: [...leadSkills] });
    setNewCert(''); setNewTech(''); setNewLead('');
    setEditModal('skills');
  };
  const toggleOpportunities = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('toggleOpportunities: no session, aborting.'); return; }
    const next = !oppOpen;
    setOppOpen(next);
    const { error } = await supabase.from('profiles').update({ open_to_opportunities: next }).eq('id', session.user.id);
    if (error) { console.error('toggleOpportunities error:', error); setOppOpen(!next); return; }
    refreshProfile();
  };

  const toggleAvailableToMentor = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('toggleAvailableToMentor: no session, aborting.'); return; }
    const next = !mentorOpen;
    setMentorOpen(next);
    const { error } = await supabase.from('profiles').update({ available_to_mentor: next }).eq('id', session.user.id);
    if (error) { console.error('toggleAvailableToMentor error:', error); setMentorOpen(!next); return; }
    refreshProfile();
  };

  const toggleSeekingMentor = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('toggleSeekingMentor: no session, aborting.'); return; }
    const next = !seekOpen;
    setSeekOpen(next);
    const { error } = await supabase.from('profiles').update({ seeking_mentor: next }).eq('id', session.user.id);
    if (error) { console.error('toggleSeekingMentor error:', error); setSeekOpen(!next); return; }
    refreshProfile();
  };

  const openMentor = () => { setModalOpen(false); setDraft({ mentorFocus }); setEditModal('mentorship'); };

  const saveCareer = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('saveCareer: no session, aborting.'); return; }
    const { error } = await supabase.from('profiles').update({
      first_name:         draft.firstName,
      last_name:          draft.lastName,
      branch:             draft.branch,
      rank:               draft.rank,
      years_of_service:   draft.yearsOfService,
      separation_date:    draft.separationDate,
      clearance:          draft.clearance,
      career_goal:        draft.careerGoal,
      career_goal_custom: draft.careerGoalCustom,
      work_type:          draft.workType,
      location:           draft.location,
      occupation_branch:  draft.occupation?.branch || occupation?.branch || null,
      occupation_code:    draft.occupation?.code   || occupation?.code   || null,
      occupation_title:   draft.occupation?.title  || occupation?.title  || null,
      career_history:     careerHistory,
    }).eq('id', session.user.id);
    if (error) { console.error('saveCareer error:', error); return; }
    setProfile({ ...draft });
    if (draft.occupation) setOccupation(draft.occupation);
    setEditModal(null);
    refreshProfile();
  };

  const saveBio = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('saveBio: no session, aborting.'); return; }
    const { error } = await supabase.from('profiles').update({ bio: draft.bio }).eq('id', session.user.id);
    if (error) { console.error('saveBio error:', error); return; }
    setProfile(p => ({ ...p, bio: draft.bio }));
    setEditModal(null);
    refreshProfile();
  };

  const saveDisplayIdentity = async (nextValue) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('saveDisplayIdentity: no session, aborting.'); return; }

    const previousValue = profile.displayIdentity;
    setDisplayIdentitySaveStatus('saving');
    setProfile(p => ({ ...p, displayIdentity: nextValue }));

    const { error } = await supabase
      .from('profiles')
      .update({ display_identity: nextValue })
      .eq('id', session.user.id);

    if (error) {
      console.error('saveDisplayIdentity error:', error);
      setProfile(p => ({ ...p, displayIdentity: previousValue }));
      setDisplayIdentitySaveStatus('error');
      setTimeout(() => setDisplayIdentitySaveStatus('idle'), 2000);
      return;
    }

    setDisplayIdentitySaveStatus('saved');
    refreshProfile();
    setTimeout(() => setDisplayIdentitySaveStatus('idle'), 1000);
  };

  const saveSkills = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('saveSkills: no session, aborting.'); return; }
    const { error } = await supabase.from('profiles').update({
      certifications: draft.certs,
      tech_skills:    draft.techSkills,
      lead_skills:    draft.leadSkills,
    }).eq('id', session.user.id);
    if (error) { console.error('saveSkills error:', error); return; }
    setCerts(draft.certs);
    setTechSkills(draft.techSkills);
    setLeadSkills(draft.leadSkills);
    setEditModal(null);
    refreshProfile();
  };

  const saveMentor = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) { console.warn('saveMentor: no session, aborting.'); return; }
    const { error } = await supabase.from('profiles').update({
      mentor_focus: draft.mentorFocus,
    }).eq('id', session.user.id);
    if (error) { console.error('saveMentor error:', error); return; }
    setMentorFocus(draft.mentorFocus);
    setEditModal(null);
    refreshProfile();
  };

  const addTag = (key, val, setVal) => {
    const v = val.trim(); if (!v) return;
    setDraft(d => ({ ...d, [key]: [...(d[key] || []), v] }));
    setVal('');
  };
  const removeTag = (key, i) => setDraft(d => ({ ...d, [key]: d[key].filter((_, j) => j !== i) }));
  const d = (key, val) => setDraft(prev => ({ ...prev, [key]: val }));

  const profileChecklist = [
    { label: 'Service branch', done: Boolean(profile.branch) },
    { label: 'Rank or grade', done: Boolean(profile.rank) },
    { label: 'Military specialty', done: Boolean(occupation?.code || occupation?.title) },
    { label: 'Personal summary', done: Boolean(profile.bio?.trim()) },
  ];
  const nextProfileItem = profileChecklist.find(item => !item.done);

  const saveVisibility = async (section, level) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const previous = profile.visibility;
    const next = { ...previous, [section]: level };
    setProfile(p => ({ ...p, visibility: next }));
    setVisibilitySaveStatus('saving');
    const { error } = await supabase.from('profiles').update({ visibility: next }).eq('id', session.user.id);
    if (error) {
      setProfile(p => ({ ...p, visibility: previous }));
      setVisibilitySaveStatus('error');
      return;
    }
    setVisibilitySaveStatus('saved');
    refreshProfile();
    setTimeout(() => setVisibilitySaveStatus('idle'), 1400);
  };

  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase();

  const EditIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );

  const VisRow = ({ label, section, level }) => (
    <div className="db-vis-row">
      <span className="db-vis-name">{label}</span>
      <select className={`db-vis-control ${level}`} value={level} onChange={e => saveVisibility(section, e.target.value)} aria-label={`${label} visibility`}>
        <option value="private">Only me</option>
        <option value="members">Members</option>
        <option value="recruiters">Employers</option>
      </select>
    </div>
  );

  const CloseBtn = () => (
    <button className="db-modal-close" onClick={() => setEditModal(null)} aria-label="Close">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  );

  return (
    <div className="dashboard-root vq-surface">
      <AppNav />
      <header className="db-page-heading">
        <svg className="db-heading-mark" viewBox="0 0 40 48" fill="none" aria-hidden="true">
          <path d="M3 3h34v24L20 44 3 27V3Z" stroke="currentColor" strokeWidth="1.5" />
          <path d="m11 18 9-6 9 6m-18 8 9-6 9 6" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <div>
          <p className="db-page-eyebrow">Your veteran career profile</p>
          <h1>Your service. Your next chapter.</h1>
          <p>Bring your military experience, civilian goals, and skills together.</p>
        </div>
        <a className="db-heading-link" href="#privacy-dashboard">Review my privacy</a>
      </header>

      <div className="db-shell">

        {/* ── MAIN COLUMN ── */}
        <main className="db-main-col">

          {/* Welcome banner / Completion banner */}
          <DBReveal delay={0}>
            {!profile.firstName ? (
              <div className="db-welcome-banner">
                <div className="db-welcome-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <div className="db-welcome-text">
                  <h3>Welcome to VetQualified</h3>
                  <p>Start with your military background and explore how it connects to civilian work.</p>
                  <div className="db-welcome-actions">
                    <button className="db-welcome-primary" onClick={openCareer}>Build My Profile</button>
                    <button className="db-welcome-secondary" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>See How It Works</button>
                  </div>
                  <span className="db-welcome-microcopy">Start with the experience you want to share.</span>
                </div>
              </div>
            ) : (
              <div className="db-completion-banner">
                <div className="db-completion-text">
                  <h3>{nextProfileItem ? 'Keep building your service story' : 'Your core Mission Background is in place'}</h3>
                  <p>{nextProfileItem ? `Next suggested step: add your ${nextProfileItem.label.toLowerCase()}.` : 'You can revisit any section whenever your story changes.'}</p>
                  <div className="db-profile-checklist">
                    {profileChecklist.map(item => (
                      <span key={item.label} className={item.done ? 'done' : ''}>
                        <span className="db-profile-checkmark">{item.done ? '✓' : '·'}</span> {item.label}
                      </span>
                    ))}
                  </div>
                </div>
                {nextProfileItem && <button className="db-complete-btn" onClick={nextProfileItem.label === 'Personal summary' ? openBio : openCareer}>Add next detail</button>}
              </div>
            )}
          </DBReveal>

          {/* Mission Background */}
          <DBReveal delay={1}>
            <div className="db-card vq-card">
              <div className="db-card-header">
                <div><h2 className="db-card-title">Mission Background</h2><p className="db-card-kicker">Built only from details you entered · self-reported unless marked otherwise</p></div>
                <button className="db-edit-btn" onClick={openCareer}><EditIcon /> Edit</button>
              </div>
              <p className="db-mission-story">
                {profile.branch ? `You recorded service in the ${profile.branch}` : 'Add your branch to begin your service story'}
                {profile.rank ? `, reaching ${profile.rank}` : ''}
                {profile.yearsOfService ? ` across ${profile.yearsOfService} ${Number(profile.yearsOfService) === 1 ? 'year' : 'years'}` : ''}
                {occupation?.title ? `. Your recorded specialty is ${occupation.title}${occupation.code ? ` (${occupation.code})` : ''}` : ''}.
              </p>
              <div className="db-detail-grid">
                <div className="db-detail-item" style={{ gridColumn: '1 / -1' }}>
                  {occupation?.code ? (
                    <div className="db-occ-display">
                      <label>Military Occupation</label>
                      <div className="db-occ-chip">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                        <span className="db-occ-code">{occupation.code}</span>
                        <span className="db-occ-sep">·</span>
                        <span className="db-occ-title">{occupation.title}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="db-occ-empty">
                      <label>Military Occupation</label>
                      <p>No occupation selected — click Edit to add your AFSC / MOS / Rating.</p>
                    </div>
                  )}
                  {careerHistory.length > 0 && (
                    <div className="db-occ-history">
                      <label>Prior Military Jobs</label>
                      {careerHistory.map((entry, i) => (
                        <div key={i} className="db-occ-history-chip">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          </svg>
                          <span className="db-occ-code">{entry.code}</span>
                          <span className="db-occ-sep">·</span>
                          <span className="db-occ-title">{entry.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="db-detail-item">
                  <label>Clearance Level</label>
                  {profile.clearance && profile.clearance !== '0' ? (
                    <span className="db-clearance-chip">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      {profile.clearance}
                    </span>
                  ) : (
                    <p style={{ color: 'var(--db-text-muted)' }}>Not set</p>
                  )}
                </div>
                <div className="db-detail-item">
                  <label>Separation Date</label>
                  <p>{formatDate(profile.separationDate) || 'Not added yet'}</p>
                </div>
                <div className="db-detail-item">
                  <label>Highest Rank</label>
                  <p>{profile.rank || 'Not added yet'}</p>
                </div>
                <div className="db-detail-item">
                  <label>Career Goal</label>
                  <p>{(profile.careerGoal === 'Other (specify below)' ? profile.careerGoalCustom : profile.careerGoal) || 'Not selected yet'}</p>
                </div>
                <div className="db-detail-item">
                  <label>Preferred Work Type</label>
                  <p>{profile.workType || 'Not selected yet'}</p>
                </div>
              </div>
              {profile.bio ? (
                <p className="db-bio-text">{profile.bio}</p>
              ) : !profile.firstName && !occupation ? (
                <div className="db-empty-state">
                  <div className="db-empty-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                    </svg>
                  </div>
                  <p>Your military background goes here. Add your service details so recruiters can find you.</p>
                  <button className="db-empty-cta" onClick={openCareer}>Add Service Details</button>
                </div>
              ) : null}
              <button className="db-edit-btn" style={{ marginTop: '1rem' }} onClick={openBio}><EditIcon /> Edit Bio</button>
            </div>
          </DBReveal>

          {/* Civilian Translation */}
          <DBReveal delay={2}>
            <CivilianTranslation occupation={occupation} careerHistory={careerHistory} bio={profile.bio} />
          </DBReveal>

          {/* Skills & Certifications */}
          <DBReveal delay={3}>
            <div className="db-card vq-card">
              <div className="db-card-header">
                <h2 className="db-card-title">Skills &amp; Certifications</h2>
                <button className="db-edit-btn" onClick={openSkills}><EditIcon /> Edit</button>
              </div>
              {certs.length === 0 && techSkills.length === 0 && leadSkills.length === 0 ? (
                <div className="db-empty-state">
                  <div className="db-empty-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
                    </svg>
                  </div>
                  <p>Add your certifications and skills to appear in recruiter searches and improve your match score.</p>
                  <button className="db-empty-cta" onClick={openSkills}>Add Skills & Certs</button>
                </div>
              ) : (
                <>
                  <div className="db-section-label">Certifications</div>
                  <div className="db-skills-grid">{certs.map(c => <span key={c} className="db-skill-tag">{c}</span>)}</div>
                  <div className="db-section-label" style={{ marginTop: '1.25rem' }}>Technical Skills</div>
                  <div className="db-skills-grid">{techSkills.map(s => <span key={s} className="db-skill-tag">{s}</span>)}</div>
                  <div className="db-section-label" style={{ marginTop: '1.25rem' }}>Leadership &amp; Soft Skills</div>
                  <div className="db-skills-grid">{leadSkills.map(s => <span key={s} className="db-skill-tag">{s}</span>)}</div>
                </>
              )}
             
              <div className="db-resume-zone" onClick={handleResumeView}>
                <div className="db-resume-icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/>
                  </svg>
                </div>
                <div className="db-resume-text">
                  <p style={resumeName ? { color: 'var(--db-gold-bright)' } : {}}>{resumeName || 'Upload your resume'}</p>
                  <span>{resumeName ? 'Click to view original file · Replace with PDF' : 'PDF · Up to 10 MB · Shared with employers when you apply'}</span>
                </div>
                <button
                  className="db-resume-browse"
                  type="button"
                  disabled={resumeUploading}
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                >
                  {resumeUploading ? 'Uploading…' : resumeName ? 'Replace with PDF' : 'Browse PDF'}
                </button>
              </div>
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" disabled={resumeUploading} style={{ display: 'none' }} onChange={handleResumeChange} />
              <p style={{ fontSize: '0.8rem' }}>For a consistent employer preview, upload a PDF. Existing Word resumes remain available in their original format.</p>
              {resumeUploadError && <p role="alert">{resumeUploadError}</p>}
            </div>
          </DBReveal>

          {/* Public Identity */}
          <DBReveal delay={0.9}>
            <div className="db-card vq-card">
              <div className="db-card-header">
                <h2 className="db-card-title">Public Identity</h2>
                {displayIdentitySaveStatus === 'saving' && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--db-text-muted, #8FA3BE)', fontWeight: 500 }}>
                    Saving…
                  </span>
                )}
                {displayIdentitySaveStatus === 'saved' && (
                  <span style={{ fontSize: '0.78rem', color: '#4ade80', fontWeight: 500 }}>
                    Saved ✓
                  </span>
                )}
                {displayIdentitySaveStatus === 'error' && (
                  <span style={{ fontSize: '0.78rem', color: '#ef4444', fontWeight: 500 }}>
                    Save failed — try again
                  </span>
                )}
              </div>
              <div className="db-detail-item db-identity-row">
                <div className="db-identity-helper">
                  <label style={{ marginBottom: '0.35rem' }}>How You Appear Publicly</label>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--db-text-muted, #8FA3BE)', lineHeight: 1.5 }}>
                    This controls how you appear to recruiters and other members across VetQualified. Rank is never shown publicly.
                  </p>
                </div>
                <div className="db-identity-control">
                  <select
                    value={profile.displayIdentity || 'veteran'}
                    onChange={e => saveDisplayIdentity(e.target.value)}
                    disabled={displayIdentitySaveStatus === 'saving'}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: 'var(--db-text-primary, #fff)',
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.875rem',
                      cursor: displayIdentitySaveStatus === 'saving' ? 'wait' : 'pointer',
                    }}
                  >
                    <option value="veteran">Veteran</option>
                    <option value="retired_veteran">Retired Veteran</option>
                    <option value="transitioning_service_member">Transitioning Service Member</option>
                    <option value="active_duty">Active Duty</option>
                    <option value="guard_reserve">Guard / Reserve</option>
                    <option value="military_spouse">Military Spouse</option>
                    <option value="family_member">Military Family Member</option>
                    <option value="supporter">Supporter</option>
                  </select>
                </div>
              </div>
              <p style={{ marginTop: '0.85rem', marginBottom: 0, fontSize: '0.78rem', color: 'var(--db-text-muted, #8FA3BE)' }}>
                Currently appearing as: <strong style={{ color: 'var(--db-gold, #C9A84C)' }}>
                  {renderIdentity(profile.displayIdentity, profile.branch)}
                </strong>
              </p>
            </div>
          </DBReveal>

          {/* Activity & Matches */}
          <DBReveal delay={4}>
            <div className="db-card vq-card">
              <div className="db-card-header">
                <h2 className="db-card-title">Activity &amp; Matches</h2>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>All-time</span>
              </div>
              <div className="db-activity-grid">
                <div className="db-activity-cell">
                  <div className="db-activity-num">{stats.jobMatches}</div>
                  <div className="db-activity-label">Job Matches</div>
                </div>
                <div className="db-activity-cell">
                  <div className="db-activity-num">{stats.profileViews}</div>
                  <div className="db-activity-label">Profile Views</div>
                </div>
                <div className="db-activity-cell">
                  <div className="db-activity-num">{stats.recruiterViews}</div>
                  <div className="db-activity-label">Recruiter Views</div>
                </div>
              </div>
            </div>
          </DBReveal>

          {/* Mentorship */}
          <DBReveal delay={5}>
            <div className="db-card vq-card" id="mentorship-dashboard">
              <div className="db-card-header"><h2 className="db-card-title">Mentorship</h2></div>
              <div className="db-mentor-row">
                <div className="db-mentor-info"><h4>Available to Mentor</h4><p>Share your experience with transitioning veterans</p></div>
                <Toggle checked={mentorOpen} onChange={toggleAvailableToMentor} />
              </div>
              <div className="db-mentor-row">
                <div className="db-mentor-info"><h4>Seeking a Mentor</h4><p>Connect with veterans who have made your career transition</p></div>
                <Toggle checked={seekOpen} onChange={toggleSeekingMentor} />
              </div>
              <div className="db-mentor-row">
                <div className="db-mentor-info">
                  <h4>Mentorship Focus</h4>
                  <p className="db-mentor-focus">{mentorFocus || 'Add the areas where you would like to give or receive guidance.'}</p>
                </div>
                <button className="db-edit-btn" onClick={openMentor}><EditIcon /> Edit</button>
              </div>
            </div>
          </DBReveal>

        </main>

        {/* ── LEFT SIDEBAR ── */}
        <aside className="db-left-col">
          <div className="db-card db-profile-card vq-card vq-card--raised" data-branch={normalizeBranch(profile.branch) || undefined}>
            <div className="db-avatar-wrap" onClick={handleAvatarClick} style={{ cursor: 'pointer' }}>
              {profile.avatarUrl
                ? <img src={profile.avatarUrl} alt="Profile" className="db-avatar db-avatar-photo" />
                : <div className="db-avatar">{initials}</div>
              }
              <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
            </div>
            <div>
              <div className="db-member-name">{profile.firstName} {profile.lastName}</div>
              <div className="db-member-rank">{renderIdentity(profile.displayIdentity, profile.branch)}</div>
            </div>
            {/* Presentation guards only: rows render once their value exists,
                so first paint never shows orphaned labels while the profile loads. */}
            {profile.branch ? (
              <div className="db-badge-row">
                <span className="db-branch-badge">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  {profile.branch}
                </span>
              </div>
            ) : null}
            {(profile.yearsOfService || profile.location || profile.careerGoal) ? (
              <div className="db-service-meta">
                {profile.yearsOfService ? (
                  <div className="db-meta-row">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                    {profile.yearsOfService} Years of Service
                  </div>
                ) : null}
                {profile.location ? (
                  <div className="db-meta-row">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {profile.location}
                  </div>
                ) : null}
                {profile.careerGoal ? (
                  <div className="db-meta-row">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
                    Transitioning → {profile.careerGoal}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="db-opp-row">
              <div>
                <div className="db-opp-label">Open to Opportunities</div>
                <div className="db-opp-note">Visible to recruiters only</div>
              </div>
              <Toggle checked={oppOpen} onChange={toggleOpportunities} />
            </div>
          </div>

          <AnimatedStats stats={stats} />

          <div className="db-card vq-card" id="privacy-dashboard">
            <div className="db-card-header"><h2 className="db-card-title">Visibility</h2>{visibilitySaveStatus !== 'idle' && <span className={`db-save-state ${visibilitySaveStatus}`}>{visibilitySaveStatus === 'saving' ? 'Saving…' : visibilitySaveStatus === 'saved' ? 'Saved' : 'Save failed'}</span>}</div>
            <p className="db-visibility-subtitle">Choose who can see each section. Members and employers are separate audiences. Contact info and résumé stay out of search; sharing them takes a separate action.</p>
            <div className="db-vis-sections">
              <div className="db-vis-head" aria-hidden="true"><span>Profile section</span><span>Visible to</span></div>
              <VisRow label="Military Experience" section="military" level={profile.visibility.military} />
              <VisRow label="Clearance Tier" section="clearance" level={profile.visibility.clearance} />
              <VisRow label="Certifications" section="certifications" level={profile.visibility.certifications} />
              <VisRow label="Skills" section="skills" level={profile.visibility.skills} />
              <VisRow label="Contact Info" section="contact" level={profile.visibility.contact} />
              <VisRow label="Resume" section="resume" level={profile.visibility.resume} />
            </div>
            <button className="db-preview-btn" onClick={() => setModalOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview as Recruiter
            </button>
          </div>
        </aside>
      </div>

      <footer className="db-footer">
        <p>© 2025 VetQualified &nbsp;·&nbsp; <a href="/privacy">Privacy Policy</a> &nbsp;·&nbsp; <a href="/terms">Terms of Service</a> &nbsp;·&nbsp; <a href="/help">Help Center</a></p>
        <p className="db-footer-gap">Built for those who served.</p>
      </footer>

      {/* ── RECRUITER PREVIEW MODAL ── */}
      <div
        className={`db-modal-overlay${modalOpen ? ' open' : ''}`}
        onClick={e => { if (e.target === e.currentTarget) setModalOpen(false); }}
        role="dialog" aria-modal="true"
      >
        <div className="db-modal">
          <div className="db-modal-header">
            <h2 className="db-modal-title">Recruiter View Preview</h2>
            <button className="db-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <p className="db-modal-sublabel">This is what a recruiter will see when they view your profile.</p>
          <div className="db-rv-card">
            <div className="db-rv-name">{profile.firstName} {profile.lastName[0]}.</div>
            <div className="db-rv-sub">{profile.branch} · {profile.yearsOfService} Years of Service · {profile.location}</div>
            <div className="db-rv-sections">
              {[
                { k: 'Branch',         v: profile.branch,                                                          tier: 'military' },
                { k: 'Service',        v: `${profile.yearsOfService} Years · Sep. ${formatDate(profile.separationDate)}`, tier: 'military' },
                { k: 'Clearance',      v: `${profile.clearance} (self-reported)`,                                  tier: 'clearance' },
                { k: 'Open to Work',   v: `✓ Yes — ${profile.careerGoal}`,                                         tier: null, green: true },
                { k: 'Skills',         v: techSkills.slice(0,3).join(', ') + '…',                                  tier: 'skills' },
                { k: 'Certifications', v: `${certs.length} certifications on profile`,                             tier: 'certifications' },
                { k: 'Rank / Grade',   v: profile.rank,                                                            tier: 'military' },
                { k: 'Specialty Code', v: occupation?.code || '—',                                                 tier: 'military' },
                { k: 'Contact Info',   v: '— Send a message request.',                                             forceHidden: true },
                { k: 'Resume',         v: '— Available after candidate applies.',                                   forceHidden: true },
              ].map(({ k, v, tier, green, forceHidden }) => {
                const hidden = forceHidden === true
                  ? true
                  : tier === null ? false : profile.visibility[tier] !== 'recruiters';
                return (
                  <div className="db-rv-row" key={k}>
                    <span className="db-rv-key">{k}</span>
                    <span className={hidden ? 'db-rv-hidden' : 'db-rv-val'} style={green ? { color: '#4ade80' } : {}}>
                      {hidden && !forceHidden ? '— Not visible to employers.' : v}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="db-modal-note">Your contact information and resume are never shared without your approval — a recruiter must send a message request first. Everything else follows the visibility settings in your Privacy Dashboard, above.</p>
        </div>
      </div>

      {/* ── EDIT: MILITARY & CAREER ── */}
      {editModal === 'career' && (
        <div className="db-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}>
          <div className="db-modal db-edit-modal">
            <div className="db-modal-header">
              <h2 className="db-modal-title">Military &amp; Career Profile</h2>
              <CloseBtn />
            </div>
            <div className="db-edit-form">
              <div className="db-edit-field">
                <label>Military Occupation Code</label>
                <OccupationSelector
                  value={occupation}
                  onChange={occ => setDraft(prev => ({ ...prev, occupation: occ }))}
                  profileRank={draft.rank || profile.rank}
                />
                <SecondaryOccupation entries={careerHistory} onAdd={handleAddSecondary} onRemove={handleRemoveSecondary} />
              </div>
              <div className="db-edit-row">
                <div className="db-edit-field">
                  <label>First Name</label>
                  <input type="text" value={draft.firstName || ''} onChange={e => d('firstName', e.target.value)} />
                </div>
                <div className="db-edit-field">
                  <label>Last Name</label>
                  <input type="text" value={draft.lastName || ''} onChange={e => d('lastName', e.target.value)} />
                </div>
              </div>
              <div className="db-edit-row">
                <div className="db-edit-field">
                  <label>Branch of Service</label>
                  <select value={draft.branch || ''} onChange={e => setDraft(prev => ({ ...prev, branch: e.target.value, rank: '' }))}>
                    {BRANCH_OPTIONS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="db-edit-field">
                  <label>Highest Rank</label>
                  <select value={draft.rank || ''} onChange={e => d('rank', e.target.value)}>
                    <option value="">— Select rank —</option>
                    {(RANKS_BY_BRANCH[draft.branch] || []).map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div className="db-edit-row">
                <div className="db-edit-field">
                  <label>Years of Service</label>
                  <input type="number" min="1" max="45" value={draft.yearsOfService || ''} onChange={e => d('yearsOfService', e.target.value)} placeholder="21" />
                </div>
                <div className="db-edit-field">
                  <label>Separation / Retirement Date</label>
                  <div className="db-date-wrap">
                    <input type="date" value={draft.separationDate || ''} onChange={e => d('separationDate', e.target.value)} />
                    {draft.separationDate && <span className="db-date-preview">{formatDate(draft.separationDate)}</span>}
                  </div>
                </div>
              </div>
              <div className="db-edit-field">
                <label>Clearance Level</label>
                <select value={draft.clearance || ''} onChange={e => d('clearance', e.target.value)}>
                  {CLEARANCE_OPTIONS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="db-edit-field" style={{ position: "relative" }}>
                <label>Location</label>
                <input
                  type="text"
                  value={draft.location || ''}
                  onChange={(e) => {
  const query = e.target.value;
  setDraft(prev => ({ ...prev, location: query }));
  if (!query || query.length < 2) { setLocationSuggestions([]); return; }

  if (!window.google?.maps?.places) {
    setLocationSuggestions([]);
    return;
  }

  const service = new window.google.maps.places.AutocompleteService();
  service.getPlacePredictions(
    {
      input: query,
      types: ['(cities)'],
      componentRestrictions: { country: 'us' },
    },
    (predictions, status) => {
      if (
        status !== window.google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        setLocationSuggestions([]);
        return;
      }
      setLocationSuggestions(predictions.map(p => p.description));
    }
  );
}}
                  placeholder="City, State"
                  autoComplete="off"
                />
                {locationSuggestions.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#122847", border: "1px solid rgba(201,168,76,0.25)", borderRadius: 8, zIndex: 999, marginTop: 4, overflow: "hidden" }}>
                    {locationSuggestions.map((s, i) => (
                      <div
                        key={i}
                        onClick={() => { d('location', s); setLocationSuggestions([]); }}
                        style={{ padding: "0.6rem 0.9rem", fontSize: "0.85rem", color: "#CBD5E1", cursor: "pointer", borderBottom: i < locationSuggestions.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(201,168,76,0.1)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="db-edit-field">
                <label>Career Goal</label>
                <select value={draft.careerGoal || ''} onChange={e => d('careerGoal', e.target.value)}>
                  <option value="">— Select a goal —</option>
                  {CAREER_GOAL_OPTIONS.map(g => <option key={g}>{g}</option>)}
                </select>
                {draft.careerGoal === 'Other (specify below)' && (
                  <input type="text" style={{ marginTop: '0.5rem' }} value={draft.careerGoalCustom || ''} onChange={e => d('careerGoalCustom', e.target.value)} placeholder="Describe your target role..." />
                )}
              </div>
              <div className="db-edit-field">
                <label>Preferred Work Type</label>
                <div className="db-radio-group">
                  {WORK_TYPE_OPTIONS.map(opt => (
                    <label key={opt.value} className={`db-radio-option ${draft.workType === opt.value ? 'selected' : ''}`}>
                      <input type="radio" name="workType" value={opt.value} checked={draft.workType === opt.value} onChange={() => d('workType', opt.value)} />
                      <div className="db-radio-content">
                        <span className="db-radio-label">{opt.label}</span>
                        <span className="db-radio-desc">{opt.desc}</span>
                      </div>
                      <div className="db-radio-dot"></div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="db-edit-footer">
              <button className="db-edit-cancel" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="db-edit-save" onClick={saveCareer}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT: BIO ── */}
      {editModal === 'bio' && (
        <div className="db-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}>
          <div className="db-modal db-edit-modal">
            <div className="db-modal-header">
              <h2 className="db-modal-title">Edit Bio / Summary</h2>
              <CloseBtn />
            </div>
            <div className="db-edit-form">
              <div className="db-edit-field">
                <label>Your Summary</label>
                <p className="db-edit-hint">Write in civilian language. This is what recruiters and members read to understand who you are beyond your specialty code.</p>
                <textarea rows={8} value={draft.bio || ''} onChange={e => setDraft(d => ({ ...d, bio: e.target.value }))} placeholder="Describe your background, experience, and what you're looking for next..." />
                <span className="db-char-count">{(draft.bio || '').length} characters</span>
              </div>
            </div>
            <div className="db-edit-footer">
              <button className="db-edit-cancel" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="db-edit-save" onClick={saveBio}>Save Bio</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT: SKILLS ── */}
      {editModal === 'skills' && (
        <div className="db-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}>
          <div className="db-modal db-edit-modal db-edit-modal-wide">
            <div className="db-modal-header">
              <h2 className="db-modal-title">Skills &amp; Certifications</h2>
              <CloseBtn />
            </div>
            <div className="db-edit-form">
              {[
                { key: 'certs',      label: 'Certifications',          val: newCert, setVal: setNewCert, isCert: true  },
                { key: 'techSkills', label: 'Technical Skills',         val: newTech, setVal: setNewTech, isCert: false },
                { key: 'leadSkills', label: 'Leadership & Soft Skills', val: newLead, setVal: setNewLead, isCert: false },
              ].map(({ key, label, val, setVal, isCert }) => (
                <div className="db-edit-field" key={key}>
                  <label>{label}</label>
                  <div className="db-tag-add-row">
                    <input
                      type="text" value={val}
                      onChange={e => setVal(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(key, val, setVal); }}}
                      placeholder={`Add ${label.toLowerCase()}...`}
                    />
                    <button className="db-tag-add-btn" onClick={() => addTag(key, val, setVal)}>+ Add</button>
                  </div>
                  {(draft[key] || []).length > 0 && (
                    <div className="db-tag-cloud">
                      {(draft[key] || []).map((item, i) => (
                        <span key={i} className="db-tag-chip cert">
                          {item}
                          <button className="db-tag-remove" onClick={() => removeTag(key, i)} title="Remove">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="db-edit-footer">
              <button className="db-edit-cancel" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="db-edit-save" onClick={saveSkills}>Save Skills</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT: MENTORSHIP ── */}
      {editModal === 'mentorship' && (
        <div className="db-modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setEditModal(null); }}>
          <div className="db-modal db-edit-modal">
            <div className="db-modal-header">
              <h2 className="db-modal-title">Mentorship Focus</h2>
              <CloseBtn />
            </div>
            <div className="db-edit-form">
              <div className="db-edit-field">
                <label>Focus Areas</label>
                <p className="db-edit-hint">Describe where you can help others or where you're seeking guidance. Separate topics with · or commas.</p>
                <textarea rows={4} value={draft.mentorFocus || ''} onChange={e => setDraft(d => ({ ...d, mentorFocus: e.target.value }))} placeholder="e.g. Cybersecurity career transition · Clearance jobs · Federal contracting" />
              </div>
            </div>
            <div className="db-edit-footer">
              <button className="db-edit-cancel" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="db-edit-save" onClick={saveMentor}>Save Focus</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
